import { useState } from 'react';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetQueueStatsQuery,
  useRedriveQueueMutation,
  useDiscardQueueMutation,
  type QueueEntry,
} from '@/redux/internal-services/queueAdminApi';

const QUEUE_LABELS: Record<string, string> = {
  notification: 'Notification Queue',
  scheduler: 'Scheduler Queue',
};

function QueueCard({
  name,
  label,
  entry,
  onRedrive,
  onDiscard,
}: {
  name: string;
  label: string;
  entry: QueueEntry;
  onRedrive: (queue: string, max: number) => void;
  onDiscard: (queue: string, max: number) => void;
}) {
  const [maxMessages, setMaxMessages] = useState(10);

  return (
    <div className="rounded-xl border border-ui-border bg-ui-card p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ui-text">{label}</h3>
        <div className="flex items-center gap-2">
          {entry.worker && (
            <span className="flex items-center gap-1.5 text-xs text-ui-text2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  entry.worker.alive ? 'bg-green-500' : 'bg-red-400'
                }`}
              />
              {entry.worker.alive ? 'Worker alive' : 'Worker offline'}
            </span>
          )}
        </div>
      </div>

      {/* Depth cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DepthCard label="Main Queue" value={entry.main_queue_depth} configured={entry.queue_url_configured} />
        <DepthCard label="DLQ" value={entry.dlq_depth} configured={entry.dlq_url_configured} warn={entry.dlq_depth > 0} />
        <ConfigBadge label="Queue URL" configured={entry.queue_url_configured} />
        <ConfigBadge label="DLQ URL" configured={entry.dlq_url_configured} />
      </div>

      {/* Worker details */}
      {entry.worker?.last_poll_at && (
        <p className="text-xs text-ui-text3">
          Last poll: {new Date(entry.worker.last_poll_at * 1000).toLocaleString()}
        </p>
      )}

      {/* DLQ Actions */}
      {entry.dlq_url_configured && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2 border-t border-ui-border">
          <div className="flex items-center gap-2">
            <label className="text-xs text-ui-text2 whitespace-nowrap">Max messages</label>
            <input
              type="number"
              min={1}
              max={100}
              value={maxMessages}
              onChange={(e) => setMaxMessages(Number(e.target.value))}
              className="w-20 rounded border border-gray-300 px-2 py-1 text-sm bg-ui-card text-ui-text"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onRedrive(name, maxMessages)}
              className="rounded-lg bg-blue-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-blue-700"
            >
              Redrive DLQ
            </button>
            <button
              onClick={() => onDiscard(name, maxMessages)}
              className="rounded-lg border border-red-300 text-red-600 px-3 py-1.5 text-xs font-medium hover:bg-red-50"
            >
              Discard DLQ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DepthCard({ label, value, configured, warn }: { label: string; value: number; configured: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3">
      <p className="text-xs text-ui-text2">{label}</p>
      {configured ? (
        <p className={`text-2xl font-semibold ${warn ? 'text-red-500' : 'text-ui-text'}`}>{value}</p>
      ) : (
        <p className="text-sm text-ui-text3 italic">Not configured</p>
      )}
    </div>
  );
}

function ConfigBadge({ label, configured }: { label: string; configured: boolean }) {
  return (
    <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3 flex items-center gap-2">
      <span className={`inline-block w-2 h-2 rounded-full ${configured ? 'bg-green-500' : 'bg-amber-400'}`} />
      <div>
        <p className="text-xs text-ui-text2">{label}</p>
        <p className="text-xs text-ui-text3">{configured ? 'Configured' : 'Not set'}</p>
      </div>
    </div>
  );
}

export default function QueueManagementPage() {
  const { data, isFetching, refetch } = useGetQueueStatsQuery();
  const [redrive] = useRedriveQueueMutation();
  const [discard] = useDiscardQueueMutation();
  const [toast, setToast] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const queues = data?.data ?? {};

  const showToast = (type: 'error' | 'success', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleRedrive = async (queue: string, max: number) => {
    try {
      const res = await redrive({ queue, max_messages: max }).unwrap();
      showToast('success', `Redriven ${res?.data?.redriven ?? 0} message(s) from ${QUEUE_LABELS[queue] ?? queue} DLQ.`);
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? `Failed to redrive ${QUEUE_LABELS[queue] ?? queue} DLQ.`);
    }
  };

  const handleDiscard = async (queue: string, max: number) => {
    if (!window.confirm(`Permanently discard up to ${max} messages from the ${QUEUE_LABELS[queue] ?? queue} DLQ?`)) {
      return;
    }
    try {
      const res = await discard({ queue, max_messages: max }).unwrap();
      showToast('success', `Discarded ${res?.data?.discarded ?? 0} message(s) from ${QUEUE_LABELS[queue] ?? queue} DLQ.`);
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? `Failed to discard ${QUEUE_LABELS[queue] ?? queue} DLQ.`);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ui-raised">
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {toast.type === 'success'
            ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          {toast.text}
        </div>
      )}
      <div className="border-b border-ui-border bg-ui-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-ui-text">Queue Management</h1>
            <p className="text-xs text-ui-text2 mt-0.5">
              Monitor SQS queue depths, worker health, and manage dead-letter queues across all services.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-lg border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover disabled:opacity-40"
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex-1 px-6 py-5 flex flex-col gap-4">
        {Object.keys(queues).length === 0 && !isFetching && (
          <p className="text-sm text-ui-text3">No queues found.</p>
        )}

        {Object.entries(queues).map(([name, entry]) => (
          <QueueCard
            key={name}
            name={name}
            label={QUEUE_LABELS[name] ?? name}
            entry={entry}
            onRedrive={handleRedrive}
            onDiscard={handleDiscard}
          />
        ))}
      </div>
    </div>
  );
}
